import { useState } from 'react';
import { PerformanceGroup } from '@/types/performance-analyze';
import { Button } from '@/components/ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Edit, Trash2, Copy } from 'lucide-react';
import { GroupFormDialog } from './GroupFormDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface GroupListProps {
  groups: PerformanceGroup[];
  onEdit: (group: PerformanceGroup) => void;
  onDelete: (groupId: string) => void;
  onCopy: (group: PerformanceGroup) => void;
}

export const GroupList = ({ groups, onEdit, onDelete, onCopy }: GroupListProps) => {
  const [symbolFilter, setSymbolFilter] = useState<string>('all');
  const [prefixFilter, setPrefixFilter] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<PerformanceGroup | null>(null);

  // Get unique symbols and prefixes
  const symbols = Array.from(new Set(groups.map(g => g.symbol)));
  const prefixes = Array.from(new Set(groups.map(g => g.prefix || 'none')));

  // Filter groups
  const filteredGroups = groups.filter(group => {
    const symbolMatch = symbolFilter === 'all' || group.symbol === symbolFilter;
    const prefixMatch = prefixFilter === 'all' || 
      (prefixFilter === 'none' ? !group.prefix : group.prefix === prefixFilter);
    return symbolMatch && prefixMatch;
  });

  const handleDeleteClick = (group: PerformanceGroup) => {
    setSelectedGroup(group);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedGroup) {
      onDelete(selectedGroup.id);
    }
    setDeleteDialogOpen(false);
    setSelectedGroup(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <Select value={symbolFilter} onValueChange={setSymbolFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by Symbol" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Symbols</SelectItem>
            {symbols.map(symbol => (
              <SelectItem key={symbol} value={symbol}>{symbol}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={prefixFilter} onValueChange={setPrefixFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by PREFIX" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All PREFIXes</SelectItem>
            <SelectItem value="none">No PREFIX</SelectItem>
            {prefixes.filter(p => p !== 'none').map(prefix => (
              <SelectItem key={prefix} value={prefix}>{prefix}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Symbol</TableHead>
              <TableHead>PREFIX</TableHead>
              <TableHead>Strategy</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>TP Values</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredGroups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  No groups found
                </TableCell>
              </TableRow>
            ) : (
              filteredGroups.map(group => (
                <TableRow key={group.id}>
                  <TableCell className="font-medium">{group.symbol}</TableCell>
                  <TableCell>{group.prefix || '-'}</TableCell>
                  <TableCell>{group.strategy}</TableCell>
                  <TableCell>{group.size_group}</TableCell>
                  <TableCell>{group.tp_values.join(', ')}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(group)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onCopy(group)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteClick(group)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this group? This action cannot be undone.
              <br />
              <br />
              <strong>
                {selectedGroup?.symbol}-{selectedGroup?.prefix || 'No Prefix'}-
                {selectedGroup?.strategy}-{selectedGroup?.size_group}
              </strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
